import React, { useEffect, useState } from 'react';
import {
    Descriptions,
    Button,
    Modal,
    Form,
    InputNumber,
    Select,
    message,
    Spin,
    Table,
    Typography,
    Input,
} from 'antd';
import { CALL_FEATCH_API } from 'common/API';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getUserInfo } from 'redux/selectors';
import styled from 'styled-components';
import dayjs from 'dayjs';

const { Option } = Select;
const { Text } = Typography;

const EditButton = styled(Button)`
    background-color: #00685f !important;
    border-color: #00685f !important;
    color: #fff !important;

    &:hover,
    &:focus {
        background-color: #00685f !important;
        border-color: #00685f !important;
        color: #fff !important;
    }
`;
const SaveButton = styled(Button)`
  background: #00685f !important;
  border-color: #00685f !important;
  color: #fff !important;

  &:hover,
  &:focus,
  &:active {
    background: #00524b !important;
    border-color: #00524b !important;
    color: #fff !important;
  }
`;
const CancelButton = styled(Button)`
  background: #ffffff !important;
  border-color: #000000 !important;
  color: #000000 !important;

  &:hover,
  &:focus,
  &:active {
    background: #ffffff !important;
  border-color: #000000 !important;
  color: #000000 !important;
  }
`;

const CurrencyManager = () => {
    const [latestCurrency, setLatestCurrency] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [form] = Form.useForm();
    const [fullCurrencyList, setFullCurrencyList] = useState([]);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState('');

    const userInfo = useSelector(getUserInfo);

    const fetchCurrencyData = async () => {
        setLoading(true);
        try {
            const response = await CALL_FEATCH_API('insyts/timesheet/currencylist', 'get');
            const currencyResponse = response?.response;

            if (currencyResponse?.code === 'Success' && Array.isArray(currencyResponse?.data)) {
                const sortedData = currencyResponse.data
                    .slice()
                    .sort((a, b) => new Date(b.date) - new Date(a.date));
                const latest = sortedData[0];
                setLatestCurrency({
                    ...latest,
                    date: moment(latest?.date).format('YYYY-MM-DD'),
                });
                setFullCurrencyList(sortedData);
            } else {
                setLatestCurrency(null);
                setFullCurrencyList([]);
            }
        } catch (error) {
            console.error('Failed to fetch currency data', error);
            message.error('Failed to load currency data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrencyData();
    }, []);

    const handleEdit = () => {
        if (!latestCurrency) return;
        const firstEditableCurrency = Object.keys(latestCurrency).find(
            (k) => k !== 'id' && k !== 'date' && k !== 'us_dollar' && k !== 'updated_currency'
        );
        setModalVisible(true);
        form.setFieldsValue({
            currency: firstEditableCurrency,
            amount: latestCurrency[firstEditableCurrency] ?? null,
        });
    };

    const handleUpdate = async () => {
        try {
            const values = await form.validateFields();
            const currencyKey = values.currency;
            const oldValueRaw = latestCurrency?.[currencyKey];

            const payload = {
                id: latestCurrency?.id,
                currency: currencyKey,
                amount: values.amount,
                old_value: oldValueRaw,
                updated_at: new Date().toISOString(),
            };

            setUpdateLoading(true);
            const res = await CALL_FEATCH_API('insyts/timesheet/updatecurrencyValue', 'post', payload);

            if (res?.response?.code === 'Success') {
                message.success('Currency updated successfully');
                setModalVisible(false);
                await fetchCurrencyData();
            } else {
                message.error('Failed to update currency');
            }
        } catch (err) {
            console.error(err);
            message.error('Validation / update failed');
        } finally {
            setUpdateLoading(false);
        }
    };

    const buildHistoryForCurrency = (currencyKey) => {
        if (!Array.isArray(fullCurrencyList) || fullCurrencyList.length === 0) return [];

        const sorted = fullCurrencyList
            .slice()
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .filter((entry) => entry[currencyKey] !== undefined && entry[currencyKey] !== null);

        const reconstructed = [];
        for (let i = 0; i < sorted.length; i++) {
            const current = sorted[i];
            const next = sorted[i + 1];
            const newVal = current[currencyKey];
            const oldVal = next ? next[currencyKey] : null;

            if (oldVal !== null && String(oldVal) !== String(newVal)) {
                reconstructed.push({
                    user: userInfo?.name || 'N/A',
                    date: current.updated_at || current.date,
                    old_value: oldVal,
                    new_value: newVal,
                });
            }
        }

        return reconstructed;
    };

    const showCurrencyHistory = (currencyKey) => {
        setSelectedCurrency(currencyKey);
        const history = buildHistoryForCurrency(currencyKey);
        setHistoryData(history);
        setHistoryModalVisible(true);
    };

    const formatLabel = (key) =>
        key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    return (
        <div style={{ padding: 24 }}>
            <Spin spinning={loading}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 16,
                        alignItems: 'center',
                    }}
                >
                    <div>
                        <h2 style={{ margin: 0, display: 'inline-block' }}>Latest Currency Rates</h2>
                        <Text style={{ color: 'red', marginLeft: 12, fontSize: 12 }}>To the USD</Text>
                    </div>

                    <EditButton
                        onClick={handleEdit}
                        disabled={!latestCurrency}
                    >
                        Edit
                    </EditButton>
                </div>

                {latestCurrency && (
                    <Descriptions bordered size="middle" column={1} labelStyle={{ fontWeight: 500 }}>
                        <Descriptions.Item label="Date">{dayjs(latestCurrency.date).format("DD-MM-YYYY")}</Descriptions.Item>

                        {Object.keys(latestCurrency)
                            .filter(
                                (key) =>
                                    key !== 'id' &&
                                    key !== 'date' &&
                                    key !== 'updated_currency' // removed from table
                            )
                            .map((key) => (
                                <Descriptions.Item label={formatLabel(key)} key={key}>
                                    <div
                                        onClick={() => key !== 'us_dollar' && showCurrencyHistory(key)}
                                        style={{
                                            cursor: key !== 'us_dollar' ? 'pointer' : 'default',
                                            color: key === 'us_dollar' ? 'gray' : '#1890ff',
                                            userSelect: 'none',
                                        }}
                                        title={key === 'us_dollar' ? 'USD is not editable' : 'Click to view history'}
                                    >
                                        {latestCurrency[key]}
                                    </div>
                                </Descriptions.Item>
                            ))}
                    </Descriptions>
                )}

                {/* Edit Modal */}
                <Modal
                    title="Edit Currency"
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    footer={[
                        <CancelButton
                            key="cancel"
                            onClick={() => setModalVisible(false)}
                            style={{
                                width: 130,
                                height: 34,
                                borderRadius: 8,
                            }}
                        >
                            Cancel
                        </CancelButton>,
                        <SaveButton
                            key="update"
                            type="primary"
                            loading={updateLoading}
                            onClick={handleUpdate}
                            style={{
                                width: 130,
                                height: 34,
                                borderRadius: 8,
                                backgroundColor: "#00685F",
                                borderColor: "#00685F",
                                color: "#fff",
                            }}
                        >
                            Update
                        </SaveButton>,
                    ]}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item name="currency" label="Currency" rules={[{ required: true }]}>
                            <Select
                                placeholder="Select Currency"
                                onChange={(value) => {
                                    const selectedAmount = latestCurrency?.[value];
                                    form.setFieldsValue({ amount: selectedAmount });
                                }}
                            >
                                {latestCurrency &&
                                    Object.keys(latestCurrency)
                                        .filter(
                                            (key) =>
                                                key !== 'id' &&
                                                key !== 'date' &&
                                                key !== 'us_dollar' &&
                                                key !== 'updated_currency' // removed from select
                                        )
                                        .map((key) => (
                                            <Option value={key} key={key}>
                                                {formatLabel(key)}
                                            </Option>
                                        ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            shouldUpdate={(prev, cur) => prev.currency !== cur.currency}
                            noStyle
                        >
                            {() => (
                                <Form.Item
                                    name="amount"
                                    label="Amount"
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (!value) return Promise.resolve();
                                                if (!/^\d+(\.\d+)?$/.test(value)) {
                                                    return Promise.reject("Please enter a valid number");
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <Input
                                        style={{ width: '100%' }}
                                        // step="0.0000001"
                                        // min={0}
                                        // stringMode
                                        onKeyDown={(e) => {
                                            if (!/[\d.]/.test(e.key) && e.key !== "Backspace") {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                </Form.Item>
                            )}
                        </Form.Item>
                    </Form>
                </Modal>

                {/* History Modal */}
                <Modal
                    title={
                        <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 18 }}>
                            History for {selectedCurrency ? formatLabel(selectedCurrency) : ''}
                        </div>
                    }
                    open={historyModalVisible}
                    onCancel={() => setHistoryModalVisible(false)}
                    footer={null}
                    width={850}
                    centered
                    bodyStyle={{
                        background: '#fafafa',
                        borderRadius: 12,
                        padding: '24px 16px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                            width: '100%',
                        }}
                    >
                        <div
                            style={{
                                width: '95%',
                                background: 'white',
                                borderRadius: 12,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                padding: 8,
                                overflow: 'hidden',
                            }}
                        >
                            <Table
                                dataSource={historyData}
                                columns={[
                                    {
                                        title: <div style={{ textAlign: 'center', fontWeight: 600 }}>User</div>,
                                        dataIndex: 'user',
                                        key: 'user',
                                        align: 'center',
                                        render: (text) => text || userInfo?.name || 'N/A',
                                    },
                                    {
                                        title: <div style={{ textAlign: 'center', fontWeight: 600 }}>Date & Time</div>,
                                        dataIndex: 'date',
                                        key: 'date',
                                        align: 'center',
                                        render: (text) =>
                                            text ? moment(text).format('YYYY-MM-DD hh:mm A') : 'N/A',
                                    },
                                    {
                                        title: <div style={{ textAlign: 'center', fontWeight: 600 }}>Old Value</div>,
                                        dataIndex: 'old_value',
                                        key: 'old_value',
                                        align: 'center',
                                    },
                                    {
                                        title: <div style={{ textAlign: 'center', fontWeight: 600 }}>New Value</div>,
                                        dataIndex: 'new_value',
                                        key: 'new_value',
                                        align: 'center',
                                    },
                                ]}
                                rowKey={(record, index) => `${record.date || index}-${index}`}
                                pagination={false}
                                size="middle"
                                bordered
                                scroll={{ y: 400 }}
                                style={{
                                    borderRadius: 12,
                                }}
                            />
                        </div>
                    </div>
                </Modal>

            </Spin>
        </div>
    );
};

export default CurrencyManager;
